/**
 * Присвоить бесплатный аудит организации.
 *
 * Дыра, которую этот экран закрывает. Бесплатный аудит — вход в воронку (спека
 * §4): он запускается без сессии, живёт семь дней по публичной ссылке и никому
 * не принадлежит. Токен происхождения (`X-Seeno-Audit-Session`) экран аудита
 * исправно сохранял и в регистрацию передавал, но у **вошедшего** человека
 * забрать отчёт было нечем: `POST /audits/claim` из интерфейса не вызывался
 * нигде, а кнопка на публичном отчёте вела на регистрацию, с которой вошедшего
 * маршрут `/auth/*` тут же уводил на обзор. Клиент с готовым отчётом и готовым
 * аккаунтом попадал в кольцо.
 *
 * **Присвоение стоит квоты.** Контракт списывает метрику `audits` (`QuotaMetric`),
 * и об этом здесь сказано до нажатия, а не после. Сегодня потолка у метрики нет
 * (`limit: null` — §10 спеки тариф её не ограничивает, частоту держит
 * ограничитель запросов), поэтому честный показ — это «столько-то забрано,
 * тарифом не ограничено», а не выдуманная полоса до придуманного лимита.
 * Полоса появится сама, когда сервер начнёт присылать `limit`.
 *
 * **Право на присвоение даёт не ссылка.** Ссылка публична и пересылается: по ней
 * отчёт смотрят. Забирает его тот, кто аудит запускал (`session_token` из этой
 * вкладки) или подтвердил почту, на которую отчёт ушёл. Поэтому экран, у
 * которого токена происхождения нет, говорит об этом заранее и не делает вид,
 * что кнопка сработает.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Meter } from '../ui/Meter';
import { Panel } from '../ui/form';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import { useAccess, useSession } from '../session';
import { useScope } from '../scope';
import { idempotencyKey } from '../api/client';
import { ApiError } from '../api/problem';
import {
  claimAudit,
  getPublicAudit,
  getQuotaUsage,
  type Audit,
  type QuotaUsage,
} from '../api/audits';
import { AUDIT_SESSION_KEY, AUDIT_SLUG_KEY } from './Audit';
import { forgetClaim, pendingClaim } from '../pending-claim';
import {
  countOf,
  dateProse,
  daysLeft,
  score as scoreFmt,
  statusLadder,
} from '@shared/src/format';
import { AUDIT, PUBLIC_REPORT_LIFETIME } from '@shared/src/product';

type Load =
  | { kind: 'loading' }
  | { kind: 'nothing' }
  | { kind: 'ready'; audit: Audit; usage: QuotaUsage | null }
  | { kind: 'failed'; error: unknown };

export function AuditClaim() {
  const session = useSession();
  const access = useAccess();
  const scope = useScope();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Слаг берётся из адреса, а если его там нет — из вкладки, в которой аудит
  // запускали. Адрес важнее: по нему приходят с публичного отчёта, и он
  // заведомо про тот аудит, который человек сейчас смотрит.
  //
  // Третий источник — отложенное намерение (`pending-claim.ts`). Оно нужно
  // ровно там, где первых двух нет: подтверждение почты открывают из письма,
  // как правило в другой вкладке, где `sessionStorage` пуст.
  const slug =
    params.get('slug') ?? sessionStorage.getItem(AUDIT_SLUG_KEY) ?? pendingClaim();
  const sessionToken = sessionStorage.getItem(AUDIT_SESSION_KEY);

  const [load, setLoad] = useState<Load>(
    slug ? { kind: 'loading' } : { kind: 'nothing' },
  );
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [claimed, setClaimed] = useState<Audit | null>(null);
  const [workspaceId, setWorkspaceId] = useState('');

  useEffect(() => {
    if (!slug) return;
    const abort = new AbortController();

    // Расход спрашивается только у того, кто до него дотягивается: `/billing/**`
    // матрица отдаёт владельцу, а присваивать аудит может ещё и аналитик.
    // Отказ в расходе экран не роняет — он про присвоение, а не про биллинг.
    const usage = access.reaches('GET', '/billing/quota')
      ? getQuotaUsage(abort.signal).catch(() => null)
      : Promise.resolve(null);

    Promise.all([getPublicAudit(slug, abort.signal), usage])
      .then(([audit, quota]) => setLoad({ kind: 'ready', audit, usage: quota }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });

    return () => abort.abort();
    // `access` — объект из мемо сессии, в зависимости не годится: он меняется
    // ссылкой на каждой смене состояния и перезапускал бы загрузку по кругу.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, attempt]);

  const retry = useCallback(() => {
    setLoad({ kind: 'loading' });
    setAttempt((value) => value + 1);
  }, []);

  const workspaces = scope.state.kind === 'ready' ? scope.state.workspaces : [];

  async function submit() {
    if (load.kind !== 'ready' || !slug) return;
    setBusy(true);
    setFailure(null);
    try {
      const target = workspaceId || workspaces[0]?.id || null;
      const audit = await claimAudit(
        {
          slug,
          ...(sessionToken ? { session_token: sessionToken } : {}),
          ...(target ? { workspace_id: target } : {}),
        },
        idempotencyKey(),
      );
      // Аудит больше не «висит» за вкладкой: он принадлежит организации, и
      // повторное присвоение только упрётся в 409. Токен происхождения тем
      // более больше не нужен — он одноразовое доказательство, а не данные.
      sessionStorage.removeItem(AUDIT_SLUG_KEY);
      sessionStorage.removeItem(AUDIT_SESSION_KEY);
      forgetClaim();
      setClaimed(audit);
      // Область перечитывается: присвоение могло завести бренд, и список
      // брендов в переключателе обязан о нём узнать.
      scope.reload();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="screen-head">
      <h1>Забрать отчёт</h1>
    </div>
  );

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (load.kind === 'nothing') {
    return (
      <>
        {head}
        <Empty
          eyebrow="Нечего забирать"
          title="Отчёт не выбран"
          action={
            <a className="btn btn-primary" href="/audit">
              Запустить бесплатный аудит
            </a>
          }
        >
          Сюда приходят с готового публичного отчёта бесплатного аудита. Запустите аудит —
          он бесплатный и занимает {AUDIT.duration}, — а потом заберите результат в эту
          организацию.
        </Empty>
      </>
    );
  }

  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ClaimError
          error={load.error}
          onRetry={retry}
          organization={session.organization?.name}
        />
      </>
    );
  }

  const { audit, usage } = load;

  /* --- присвоено --- */
  if (claimed) {
    return (
      <>
        {head}
        <Panel
          title="Отчёт у вас"
          lead={`«${claimed.brand_name}» теперь принадлежит организации${
            session.organization?.name ? ` «${session.organization.name}»` : ''
          }. Разовый замер стал точкой отсчёта: дальше продукт меряет то же самое каждую неделю.`}
        >
          <p className="notice ok" role="status">
            <b>Готово</b>
            Аудит присвоен, из квоты списан один. Публичная ссылка продолжает работать до
            конца своего срока — её никто не отзывал.
          </p>
          <div className="row-actions">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={() => navigate('/brands')}
            >
              К брендам
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => navigate('/')}
            >
              К обзору бренда
            </button>
          </div>
        </Panel>
      </>
    );
  }

  /* --- забирать нечего: аудит не в том состоянии --- */
  const unusable = unusableReason(audit);
  if (unusable) {
    return (
      <>
        {head}
        <Empty
          eyebrow="Забрать нельзя"
          title={unusable.title}
          action={
            <a className="btn btn-ghost" href="/audit">
              Запустить аудит заново
            </a>
          }
        >
          {unusable.text}
        </Empty>
      </>
    );
  }

  const audits = usage?.counters.find((counter) => counter.metric === 'audits') ?? null;
  const multiple = workspaces.length > 1;

  return (
    <>
      {head}

      {failure ? (
        <ClaimError
          error={failure}
          onRetry={() => setFailure(null)}
          organization={session.organization?.name}
        />
      ) : null}

      <Panel
        title="Что забираем"
        lead="Разовый замер становится первой точкой ряда: дальше тот же бренд меряется каждую неделю, и балл получает динамику."
      >
        <div className="audit-score">
          <div className="value tnum">
            {audit.score === null || audit.score === undefined
              ? '—'
              : scoreFmt(audit.score)}
          </div>
          <div className="audit-status">
            {audit.score === null || audit.score === undefined
              ? 'Не измерено'
              : statusLadder(audit.score)}
          </div>
        </div>

        {/* Основание под баллом обязательно: балл без него в этом продукте не
            показывается нигде (закон 7 дизайн-системы). */}
        <p className="stamp">
          {audit.brand_name} · {audit.website_display || audit.website_domain} · Индекс
          ИИ-видимости ·{' '}
          {countOf(audit.questions_planned, 'вопрос', 'вопроса', 'вопросов')} ·{' '}
          {countOf(audit.engines?.length ?? 0, 'движок', 'движка', 'движков')} · собран{' '}
          {dateProse(audit.ready_at ?? audit.created_at)}
          {/* `daysLeft` уже возвращает «через N дней» — своё «ещё» здесь
              складывалось с ним в «действует ещё через 5 дней». */}
          {audit.public_until
            ? ` · публичная ссылка истекает ${daysLeft(audit.public_until)}`
            : ''}
        </p>
      </Panel>

      <Panel
        title="Во что это обойдётся"
        lead="Присвоение списывает один аудит из квоты организации. Мы говорим это до нажатия, а не после."
      >
        {audits ? (
          <Meter
            label="Аудиты расчётного периода"
            used={audits.used}
            limit={audits.limit ?? null}
            unit={['аудит', 'аудита', 'аудитов']}
            hint={usage ? `период до ${dateProse(usage.ends_at)}` : undefined}
          />
        ) : (
          <p className="stamp">
            {access.reaches('GET', '/billing/quota')
              ? 'Сколько аудитов уже потрачено, сейчас не показать. Списание всё равно будет — один аудит.'
              : 'Расход по квоте виден владельцу организации. Присвоение спишет один аудит.'}
          </p>
        )}
      </Panel>

      {multiple ? (
        <Panel
          title="Куда положить"
          lead="Бренд аудита заведётся в этом пространстве. Перенести его потом можно, но проще выбрать сразу."
        >
          <div className="field">
            <label htmlFor="claim-workspace">Пространство</label>
            <select
              id="claim-workspace"
              value={workspaceId || workspaces[0]?.id || ''}
              onChange={(event) => setWorkspaceId(event.target.value)}
            >
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
            <span className="hint">
              Пространств в организации {workspaces.length} — выберите то, чьим клиентом
              этот бренд является
            </span>
          </div>
        </Panel>
      ) : null}

      <Panel
        title="Забрать"
        lead={
          sessionToken
            ? 'Отчёт запускали из этой вкладки — право забрать его подтверждается автоматически.'
            : 'Отчёт запускали не из этой вкладки — право забрать его придётся подтвердить.'
        }
      >
        {sessionToken ? null : (
          <p className="notice warn" role="status">
            <b>Возможно, не получится</b>
            Забрать аудит может тот, кто его запускал, или тот, кто подтвердил почту, на
            которую ушёл отчёт. Открывшему пересланную ссылку сервис откажет — это не
            ошибка, а защита от присвоения чужого отчёта.
          </p>
        )}

        <div className="row-actions">
          <button
            className="btn btn-primary"
            type="button"
            disabled={busy}
            onClick={submit}
          >
            {busy ? 'Забираем…' : 'Забрать в организацию'}
          </button>
          <Link className="btn btn-ghost" to="/">
            Не сейчас
          </Link>
        </div>
      </Panel>
    </>
  );
}

/** Состояния аудита, из которых забирать нечего. */
function unusableReason(audit: Audit): { title: string; text: string } | null {
  if (audit.claimed) {
    return {
      title: 'Этот отчёт уже забрали',
      text: 'Аудит принадлежит организации — этой или другой. Второй раз он не присваивается: у одного отчёта один владелец.',
    };
  }
  if (audit.state === 'expired') {
    return {
      title: 'Публичная ссылка истекла',
      text: `${PUBLIC_REPORT_LIFETIME} Этот срок уже прошёл. Аудит можно запустить заново — это бесплатно.`,
    };
  }
  if (audit.state === 'failed') {
    return {
      title: 'Аудит не собрался',
      text: 'Движки не ответили или сайт не удалось разобрать. Забирать нечего.',
    };
  }
  if (
    audit.state === 'preparing' ||
    audit.state === 'queued' ||
    audit.state === 'running'
  ) {
    return {
      title: 'Аудит ещё считается',
      text: `Замер занимает ${AUDIT.duration}. Вернитесь к публичному отчёту, дождитесь результата и забирайте — он никуда не денется.`,
    };
  }
  return null;
}

/**
 * Отказы присвоения, у которых есть человеческий смысл помимо общего текста.
 *
 * Ветвление по `code`, а не по статусу: так требует контракт, и один и тот же
 * `403` приходит и на чужую ссылку, и на роль без права.
 */
function ClaimError({
  error,
  onRetry,
  organization,
}: {
  error: unknown;
  onRetry: () => void;
  organization?: string;
}) {
  const again = (
    <button className="btn btn-ghost btn-sm" type="button" onClick={onRetry}>
      Повторить
    </button>
  );

  if (!(error instanceof ApiError)) return <ErrorNotice error={error} action={again} />;

  if (error.code === 'quota_exceeded' || error.code === 'plan_limit_reached') {
    return (
      <div className="notice warn" role="alert">
        <b>Квота аудитов кончилась</b>
        {organization
          ? `Организация «${organization}» забрала все аудиты расчётного периода. `
          : 'Все аудиты расчётного периода уже забраны. '}
        Отчёт при этом никуда не делся: публичная ссылка работает до конца своего срока, и
        забрать его можно, когда начнётся следующий период или вырастет тариф. Числа,
        которые уже собраны, от этого не меняются.
        {again}
      </div>
    );
  }

  if (error.problem.status === 403) {
    return (
      <div className="notice warn" role="alert">
        <b>Забрать этот отчёт нельзя</b>
        Забрать отчёт может тот, кто его запускал, или владелец почты, на которую он
        отправлен. Если ссылку прислали вам — запустите аудит по этому сайту заново, это
        бесплатно.
      </div>
    );
  }

  if (error.problem.status === 409) {
    return (
      <div className="notice warn" role="alert">
        <b>Отчёт уже забрали</b>У одного аудита один владелец, и он уже назначен. Если это
        сделал коллега, бренд уже в организации — загляните в раздел «Бренды».
      </div>
    );
  }

  if (error.problem.status === 410 || error.code === 'gone') {
    return (
      <div className="notice warn" role="alert">
        <b>Срок ссылки вышел</b>
        {PUBLIC_REPORT_LIFETIME} Аудит можно запустить заново — это бесплатно, и числа
        будут свежее.
      </div>
    );
  }

  if (error.problem.status === 404) {
    return (
      <div className="notice warn" role="alert">
        <b>Такого отчёта нет</b>
        Ссылка набрана с ошибкой или отчёт уже удалён по сроку. Проверьте адрес или
        запустите аудит заново.
      </div>
    );
  }

  return <ErrorNotice error={error} action={again} />;
}
